import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  AvatarGroup,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { apiClient } from '../api/client';
import { BOARD_MANUAL_PATH } from '../constants/routes';
import {
  BULLETIN_POLL_INTERVAL_MS,
  type BulletinCommentRow,
  type BulletinCommentsListResponse,
  type BulletinDetailResponse,
  type BulletinStampsResponse,
} from '../constants/bulletin';
import {
  BULLETIN_STAMP_META,
  BULLETIN_STAMP_TYPES,
  bulletinStampPublicUrl,
  emptyBulletinStampsBuckets,
} from '../constants/bulletinStamps';
import type { BulletinStampType } from '../constants/bulletinStamps';
import { BulletinAttachmentTypeIcon } from '../constants/bulletinAttachmentUi';
import { resolveStaffAvatarSrc } from '../constants/defaultAvatar';
import { useAuth } from '../auth/AuthContext';
import AttachmentImagePreviewDialog from './AttachmentImagePreviewDialog';
import BulletinFormattedBody from './BulletinFormattedBody';
import BulletinRichTextEditor from './BulletinRichTextEditor';
import BulletinStampStrip from './BulletinStampStrip';
import { formatDisplayExpiresAt, formatPostTime, isEditedSinceCreated } from '../utils/formatPostTime';
import { upsertReaderReadAtOptimistic } from '../utils/optimisticReaderMerge';
import { useAppSnackbar } from '../snackbar/useAppSnackbar';

function isImageContentType(ct: string): boolean {
  return ct.toLowerCase().startsWith('image/');
}

const BulletinDetail: React.FC = () => {
  const { staffProfile } = useAuth();
  const myStaffId = staffProfile?.id;
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showSnackbar } = useAppSnackbar();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState<BulletinDetailResponse | null>(null);
  const [imageUrls, setImageUrls] = useState<Record<number, string>>({});
  const [previewImage, setPreviewImage] = useState<{ src: string; alt: string } | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [stampBusy, setStampBusy] = useState(false);
  const [stampPickerAnchor, setStampPickerAnchor] = useState<HTMLElement | null>(null);
  const [commentStampPicker, setCommentStampPicker] = useState<{ commentId: number; anchor: HTMLElement } | null>(
    null
  );
  const [comments, setComments] = useState<BulletinCommentRow[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [newCommentBody, setNewCommentBody] = useState('');
  const [commentSubmitBusy, setCommentSubmitBusy] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editCommentBody, setEditCommentBody] = useState('');
  const [editCommentBusy, setEditCommentBusy] = useState(false);
  const [commentDeleteId, setCommentDeleteId] = useState<number | null>(null);
  const [commentDeleteBusy, setCommentDeleteBusy] = useState(false);
  const [commentActionMenu, setCommentActionMenu] = useState<{ commentId: number; anchor: HTMLElement } | null>(
    null
  );

  const postId = id && /^\d+$/.test(id) ? Number(id) : NaN;
  const detailEtagRef = useRef<string | null>(null);
  const commentsEtagRef = useRef<string | null>(null);

  useEffect(() => {
    detailEtagRef.current = null;
    commentsEtagRef.current = null;
  }, [postId]);

  const load = useCallback(
    async (opts?: { silent?: boolean; conditional?: boolean }) => {
      const silent = opts?.silent ?? false;
      const conditional = opts?.conditional ?? false;
      if (!Number.isInteger(postId) || postId < 1) {
        setError('投稿 ID が不正です');
        if (!silent) setLoading(false);
        return;
      }
      if (!silent) {
        setLoading(true);
        setError('');
      }
      try {
        const headers: Record<string, string> = {};
        if (conditional && silent && detailEtagRef.current) {
          headers['If-None-Match'] = detailEtagRef.current;
        }
        const res = await apiClient.get<BulletinDetailResponse>(`/api/bulletin/posts/${postId}`, {
          headers,
          validateStatus: (status) => (status >= 200 && status < 300) || status === 304,
        });
        if (res.status === 304) {
          return;
        }
        const empty = emptyBulletinStampsBuckets();
        setDetail({
          ...res.data,
          stamps: { ...empty, ...res.data.stamps },
          my_stamps: res.data.my_stamps ?? [],
        });
        const etag = res.headers.etag;
        if (typeof etag === 'string') {
          detailEtagRef.current = etag;
        }
        if (!silent) {
          try {
            await apiClient.post(`/api/bulletin/posts/${postId}/read`);
            setDetail((prev) => {
              if (!prev || !staffProfile || !myStaffId || prev.post.author_staff_id === myStaffId) {
                return prev;
              }
              return {
                ...prev,
                readers: upsertReaderReadAtOptimistic(
                  prev.readers,
                  myStaffId,
                  new Date().toISOString(),
                  staffProfile,
                ),
              };
            });
          } catch {
            /* 既読失敗は画面表示を優先 */
          }
        }
      } catch (e: unknown) {
        if (!silent) {
          if (axios.isAxiosError(e) && e.response?.status === 404) {
            setError('投稿が見つかりません');
          } else {
            setError('投稿の取得に失敗しました');
          }
          setDetail(null);
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [postId, staffProfile, myStaffId]
  );

  const fetchComments = useCallback(async (opts?: { silent?: boolean; conditional?: boolean }) => {
    const silent = opts?.silent ?? false;
    const conditional = opts?.conditional ?? false;
    if (!Number.isInteger(postId) || postId < 1) return;
    if (!silent) {
      setCommentsLoading(true);
    }
    try {
      const headers: Record<string, string> = {};
      if (conditional && silent && commentsEtagRef.current) {
        headers['If-None-Match'] = commentsEtagRef.current;
      }
      const res = await apiClient.get<BulletinCommentsListResponse>(`/api/bulletin/posts/${postId}/comments`, {
        headers,
        validateStatus: (status) => (status >= 200 && status < 300) || status === 304,
      });
      if (res.status === 304) {
        return;
      }
      const empty = emptyBulletinStampsBuckets();
      setComments(
        res.data.comments.map((c) => ({
          ...c,
          stamps: { ...empty, ...c.stamps },
          my_stamps: c.my_stamps ?? [],
        }))
      );
      const etag = res.headers.etag;
      if (typeof etag === 'string') {
        commentsEtagRef.current = etag;
      }
    } catch {
      if (!silent) {
        setError('コメントの取得に失敗しました');
      }
    } finally {
      if (!silent) {
        setCommentsLoading(false);
      }
    }
  }, [postId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!detail?.post?.id) return;
    void fetchComments();
  }, [detail?.post?.id, fetchComments]);

  useEffect(() => {
    const id = window.setInterval(() => {
      void load({ silent: true, conditional: true });
      void fetchComments({ silent: true, conditional: true });
    }, BULLETIN_POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [load, fetchComments]);

  // 画像プレビュー用プリサイン URL
  useEffect(() => {
    if (!detail?.attachments.length) return;
    let cancelled = false;
    const run = async () => {
      const next: Record<number, string> = {};
      for (const a of detail.attachments) {
        if (!isImageContentType(a.content_type)) continue;
        try {
          const u = await apiClient.get<{ download_url: string }>(
            `/api/bulletin/posts/${postId}/attachments/${a.id}/url`
          );
          if (!cancelled) next[a.id] = u.data.download_url;
        } catch {
          /* プレビュー省略 */
        }
      }
      if (!cancelled) setImageUrls(next);
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [detail, postId]);

  const handleDownload = async (attachmentId: number, filename: string) => {
    try {
      const u = await apiClient.get<{ download_url: string }>(
        `/api/bulletin/posts/${postId}/attachments/${attachmentId}/url`
      );
      const a = document.createElement('a');
      a.href = u.data.download_url;
      a.download = filename;
      a.rel = 'noopener';
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      setError('ダウンロード用 URL の取得に失敗しました');
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await apiClient.delete(`/api/bulletin/posts/${postId}`);
      showSnackbar({ message: '投稿を削除しました', severity: 'success' });
      navigate(BOARD_MANUAL_PATH);
    } catch (e: unknown) {
      const msg =
        axios.isAxiosError(e) && e.response?.data && typeof e.response.data === 'object'
          ? (e.response.data as { error?: string }).error
          : null;
      showSnackbar({ message: msg || '投稿の削除に失敗しました', severity: 'error' });
      setDeleteOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleStampToggle = async (stampType: BulletinStampType, closePicker?: boolean) => {
    setStampBusy(true);
    try {
      const res = await apiClient.post<BulletinStampsResponse>(`/api/bulletin/posts/${postId}/stamps`, {
        stamp_type: stampType,
      });
      const empty = emptyBulletinStampsBuckets();
      setDetail((d) =>
        d
          ? {
              ...d,
              stamps: { ...empty, ...res.data.stamps },
              my_stamps: res.data.my_stamps,
            }
          : d
      );
      if (closePicker) setStampPickerAnchor(null);
    } catch {
      setError('スタンプの更新に失敗しました');
    } finally {
      setStampBusy(false);
    }
  };

  const handleCommentStampToggle = async (commentId: number, stampType: BulletinStampType, closePicker?: boolean) => {
    if (!Number.isInteger(postId)) return;
    setStampBusy(true);
    try {
      const res = await apiClient.post<BulletinStampsResponse>(
        `/api/bulletin/posts/${postId}/comments/${commentId}/stamps`,
        { stamp_type: stampType }
      );
      const empty = emptyBulletinStampsBuckets();
      setComments((prev) =>
        prev.map((c) =>
          c.id === commentId
            ? { ...c, stamps: { ...empty, ...res.data.stamps }, my_stamps: res.data.my_stamps ?? [] }
            : c
        )
      );
      if (closePicker) setCommentStampPicker(null);
    } catch {
      setError('スタンプの更新に失敗しました');
    } finally {
      setStampBusy(false);
    }
  };

  const handleSubmitComment = async () => {
    const t = newCommentBody.trim();
    if (!t || !Number.isInteger(postId)) return;
    setCommentSubmitBusy(true);
    try {
      const res = await apiClient.post<{ comment: BulletinCommentRow }>(`/api/bulletin/posts/${postId}/comments`, {
        body: t,
      });
      const empty = emptyBulletinStampsBuckets();
      setComments((prev) => [
        ...prev,
        {
          ...res.data.comment,
          stamps: { ...empty, ...res.data.comment.stamps },
          my_stamps: res.data.comment.my_stamps ?? [],
        },
      ]);
      setNewCommentBody('');
      await load({ silent: true });
    } catch {
      setError('コメントの送信に失敗しました');
    } finally {
      setCommentSubmitBusy(false);
    }
  };

  const handleSaveEditComment = async () => {
    if (editingCommentId == null || !Number.isInteger(postId)) return;
    const t = editCommentBody.trim();
    if (!t) return;
    setEditCommentBusy(true);
    try {
      const res = await apiClient.patch<{ comment: BulletinCommentRow }>(
        `/api/bulletin/posts/${postId}/comments/${editingCommentId}`,
        { body: t }
      );
      const empty = emptyBulletinStampsBuckets();
      setComments((prev) =>
        prev.map((c) =>
          c.id === editingCommentId
            ? {
                ...res.data.comment,
                stamps: { ...empty, ...res.data.comment.stamps },
                my_stamps: res.data.comment.my_stamps ?? [],
              }
            : c
        )
      );
      setEditingCommentId(null);
      setEditCommentBody('');
      await load({ silent: true });
    } catch {
      setError('コメントの更新に失敗しました');
    } finally {
      setEditCommentBusy(false);
    }
  };

  const handleConfirmDeleteComment = async () => {
    if (commentDeleteId == null || !Number.isInteger(postId)) return;
    setCommentDeleteBusy(true);
    try {
      await apiClient.delete(`/api/bulletin/posts/${postId}/comments/${commentDeleteId}`);
      setComments((prev) => prev.filter((c) => c.id !== commentDeleteId));
      setCommentDeleteId(null);
      await load({ silent: true });
    } catch {
      setError('コメントの削除に失敗しました');
      setCommentDeleteId(null);
    } finally {
      setCommentDeleteBusy(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Box
          sx={{
            maxWidth: 960,
            width: '100%',
            mx: 'auto',
            display: 'flex',
            justifyContent: 'center',
            py: 6,
          }}
        >
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (!Number.isInteger(postId) || postId < 1) {
    return (
      <Box sx={{ width: '100%', flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Box sx={{ maxWidth: { xs: '100%', lg: 1320 }, width: '100%', mx: 'auto' }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            投稿 ID が不正です
          </Alert>
        </Box>
      </Box>
    );
  }

  if (!detail) {
    return error ? (
      <Box sx={{ width: '100%', flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Box sx={{ maxWidth: { xs: '100%', lg: 1320 }, width: '100%', mx: 'auto' }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        </Box>
      </Box>
    ) : null;
  }

  const { post, attachments, readers, can_edit } = detail;
  const stampsState = detail.stamps ?? emptyBulletinStampsBuckets();
  const myStampSet = new Set(detail.my_stamps ?? []);

  return (
    <Box sx={{ width: '100%', flex: 1, minHeight: 0, overflow: 'auto' }}>
      <Box sx={{ maxWidth: { xs: '100%', lg: 1320 }, width: '100%', mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          {post.title}
        </Typography>
        {can_edit && (
          <>
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/bulletin/${postId}/edit`)}
            >
              編集
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteOpen(true)}
            >
              削除
            </Button>
          </>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'stretch',
          gap: 2,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Avatar
            src={resolveStaffAvatarSrc(post.author_avatar_url)}
            alt=""
            sx={{ width: 40, height: 40, fontSize: '1rem' }}
          >
            {(post.author_display_name || '?').charAt(0)}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" color="text.secondary">
              {post.author_display_name}・{formatPostTime(post.updated_at)}
              {isEditedSinceCreated(post.created_at, post.updated_at) ? '（編集済み）' : ''}
            </Typography>
            {post.display_expires_at && new Date(post.display_expires_at) > new Date() && (
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
                表示期限 {formatDisplayExpiresAt(post.display_expires_at)} まで
              </Typography>
            )}
          </Box>
        </Box>
        <Box component="div" sx={{ whiteSpace: 'pre-wrap', typography: 'body1' }}>
          <BulletinFormattedBody body={post.body} myStaffId={myStaffId} />
        </Box>
      </Paper>

      {/* 投稿カード直下・添付より上。スタンプ左・既読アバター右（狭い幅はアバターを1行下・右寄せ） */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: { xs: 1.5, sm: 1 },
        }}
      >
        <BulletinStampStrip
          stampsState={stampsState}
          myStampSet={myStampSet}
          busy={stampBusy}
          onToggle={(type) => void handleStampToggle(type)}
          onOpenPicker={(el) => setStampPickerAnchor(el)}
        />

        {readers.length > 0 ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              flexShrink: 0,
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            <AvatarGroup
              max={20}
              sx={{
                // 透過PNGの重なりで背面のアイコンが見えないよう白背景にする
                '& .MuiAvatar-root': { width: 32, height: 32, fontSize: '0.875rem', bgcolor: '#fff' },
              }}
            >
              {readers.map((r) => (
                <Tooltip
                  key={r.staff_user_id}
                  placement="top"
                  describeChild
                  title={
                    <Box>
                      <Typography variant="body2" component="div" fontWeight={600}>
                        {r.display_name || '（名前なし）'}
                      </Typography>
                      <Typography variant="caption" component="div" sx={{ opacity: 0.9, display: 'block', mt: 0.25 }}>
                        既読:{' '}
                        {new Date(r.read_at).toLocaleString('ja-JP', {
                          year: 'numeric',
                          month: 'numeric',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </Typography>
                    </Box>
                  }
                >
                  <Avatar
                    src={resolveStaffAvatarSrc(r.reader_avatar_url)}
                    alt={r.display_name ? `${r.display_name}の既読` : '既読'}
                  >
                    {(r.display_name || '?').charAt(0)}
                  </Avatar>
                </Tooltip>
              ))}
            </AvatarGroup>
          </Box>
        ) : null}
      </Box>

        {attachments.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            添付
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {attachments.map((a) => (
              <Box key={a.id}>
                {imageUrls[a.id] ? (
                  <Box>
                    <Box
                      component="button"
                      type="button"
                      onClick={() => setPreviewImage({ src: imageUrls[a.id], alt: a.original_filename })}
                      aria-label={`${a.original_filename}を拡大表示`}
                      sx={{
                        p: 0,
                        m: 0,
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        display: 'block',
                        borderRadius: 1,
                        '&:focus-visible': {
                          outline: '2px solid',
                          outlineOffset: 2,
                          outlineColor: 'primary.main',
                        },
                      }}
                    >
                      <Box
                        component="img"
                        src={imageUrls[a.id]}
                        alt={a.original_filename}
                        sx={{
                          maxWidth: 180,
                          maxHeight: 180,
                          objectFit: 'contain',
                          borderRadius: 1,
                          display: 'block',
                          pointerEvents: 'none',
                        }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75 }}>
                      <BulletinAttachmentTypeIcon
                        contentType={a.content_type}
                        originalFilename={a.original_filename}
                        fontSize="small"
                        color="action"
                        sx={{ flexShrink: 0 }}
                      />
                      <Link
                        component="button"
                        type="button"
                        title={a.original_filename}
                        onClick={() => void handleDownload(a.id, a.original_filename)}
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          textAlign: 'left',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          color: 'text.secondary',
                          typography: 'caption',
                        }}
                      >
                        {a.original_filename}
                      </Link>
                      <IconButton
                        size="small"
                        edge="end"
                        aria-label={`${a.original_filename}をダウンロード`}
                        onClick={() => void handleDownload(a.id, a.original_filename)}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      py: 0.5,
                      borderRadius: 1,
                      px: 0.5,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <BulletinAttachmentTypeIcon
                      contentType={a.content_type}
                      originalFilename={a.original_filename}
                      color="action"
                      sx={{ flexShrink: 0 }}
                    />
                    <Link
                      component="button"
                      type="button"
                      title={a.original_filename}
                      onClick={() => void handleDownload(a.id, a.original_filename)}
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        textAlign: 'left',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: 'text.primary',
                        typography: 'body2',
                      }}
                    >
                      {a.original_filename}
                    </Link>
                    <IconButton
                      size="small"
                      aria-label={`${a.original_filename}をダウンロード`}
                      onClick={() => void handleDownload(a.id, a.original_filename)}
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        </Paper>
      )}
        </Box>

        <Box
          sx={{
            width: { xs: '100%', md: 400 },
            flexShrink: 0,
            minWidth: 0,
            alignSelf: { xs: 'stretch', md: 'flex-start' },
          }}
        >
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
          コメント
        </Typography>
        {commentsLoading ? (
          <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            {comments.map((c) =>
              editingCommentId === c.id ? (
                <Box key={c.id}>
                  <BulletinRichTextEditor
                    label="本文"
                    value={editCommentBody}
                    onChange={setEditCommentBody}
                    minRows={3}
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" variant="contained" disabled={editCommentBusy || !editCommentBody.trim()} onClick={() => void handleSaveEditComment()}>
                      保存
                    </Button>
                    <Button
                      size="small"
                      disabled={editCommentBusy}
                      onClick={() => {
                        setEditingCommentId(null);
                        setEditCommentBody('');
                      }}
                    >
                      キャンセル
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box key={c.id} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <Avatar
                    src={resolveStaffAvatarSrc(c.author_avatar_url)}
                    alt=""
                    sx={{ width: 36, height: 36, fontSize: '0.9rem', flexShrink: 0 }}
                  >
                    {(c.author_display_name || '?').charAt(0)}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 0.5 }}>
                      <Typography variant="caption" color="text.secondary" component="div" sx={{ pt: 0.25 }}>
                        {c.author_display_name}・{formatPostTime(c.updated_at)}
                        {isEditedSinceCreated(c.created_at, c.updated_at) ? '（編集済み）' : ''}
                      </Typography>
                      {myStaffId !== undefined && c.author_staff_id === myStaffId && (
                        <IconButton
                          size="small"
                          aria-label="コメントの操作"
                          onClick={(e) => setCommentActionMenu({ commentId: c.id, anchor: e.currentTarget })}
                          sx={{ mt: -0.5, mr: -0.5 }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                    <Box component="div" sx={{ whiteSpace: 'pre-wrap', typography: 'body2', mt: 0.5 }}>
                      <BulletinFormattedBody body={c.body} myStaffId={myStaffId} />
                    </Box>
                    <Box sx={{ mt: 0.75 }}>
                      <BulletinStampStrip
                        stampsState={c.stamps ?? emptyBulletinStampsBuckets()}
                        myStampSet={new Set(c.my_stamps ?? [])}
                        busy={stampBusy}
                        stampImgSize={16}
                        addButtonSize={24}
                        onToggle={(type) => void handleCommentStampToggle(c.id, type)}
                        onOpenPicker={(el) => setCommentStampPicker({ commentId: c.id, anchor: el })}
                      />
                    </Box>
                  </Box>
                </Box>
              )
            )}
          </Box>
        )}
        {editingCommentId === null && (
          <>
            <BulletinRichTextEditor
              label="新規コメント"
              value={newCommentBody}
              onChange={setNewCommentBody}
              minRows={3}
            />
            <Button variant="contained" disabled={commentSubmitBusy || !newCommentBody.trim()} onClick={() => void handleSubmitComment()}>
              送信
            </Button>
          </>
        )}
      </Paper>
        </Box>
      </Box>

      <Popover
        open={Boolean(stampPickerAnchor)}
        anchorEl={stampPickerAnchor}
        onClose={() => setStampPickerAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { mt: -0.5, minWidth: 220, maxWidth: 320 } }}
      >
        <List dense disablePadding sx={{ py: 0.5 }}>
          {BULLETIN_STAMP_TYPES.map((type) => {
            const meta = BULLETIN_STAMP_META[type];
            const mine = myStampSet.has(type);
            return (
              <ListItemButton
                key={type}
                disabled={stampBusy}
                onClick={() => void handleStampToggle(type, true)}
                selected={mine}
              >
                <ListItemAvatar sx={{ minWidth: 44 }}>
                  <Box
                    component="img"
                    src={bulletinStampPublicUrl(meta.imageFile)}
                    alt=""
                    sx={{ width: 28, height: 28, objectFit: 'contain' }}
                  />
                </ListItemAvatar>
                <ListItemText
                  primary={meta.label}
                  secondary={mine ? '付けています（タップで解除）' : undefined}
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Popover>

      <Popover
        open={Boolean(commentStampPicker)}
        anchorEl={commentStampPicker?.anchor ?? null}
        onClose={() => setCommentStampPicker(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { mt: -0.5, minWidth: 220, maxWidth: 320 } }}
      >
        <List dense disablePadding sx={{ py: 0.5 }}>
          {BULLETIN_STAMP_TYPES.map((type) => {
            const meta = BULLETIN_STAMP_META[type];
            const mine =
              commentStampPicker &&
              (comments.find((x) => x.id === commentStampPicker.commentId)?.my_stamps ?? []).includes(type);
            return (
              <ListItemButton
                key={type}
                disabled={stampBusy}
                onClick={() =>
                  commentStampPicker
                    ? void handleCommentStampToggle(commentStampPicker.commentId, type, true)
                    : undefined
                }
                selected={Boolean(mine)}
              >
                <ListItemAvatar sx={{ minWidth: 44 }}>
                  <Box
                    component="img"
                    src={bulletinStampPublicUrl(meta.imageFile)}
                    alt=""
                    sx={{ width: 28, height: 28, objectFit: 'contain' }}
                  />
                </ListItemAvatar>
                <ListItemText
                  primary={meta.label}
                  secondary={mine ? '付けています（タップで解除）' : undefined}
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Popover>

      <Menu
        anchorEl={commentActionMenu?.anchor ?? null}
        open={Boolean(commentActionMenu)}
        onClose={() => setCommentActionMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 140 } }}
      >
        <MenuItem
          onClick={() => {
            if (commentActionMenu) {
              const row = comments.find((x) => x.id === commentActionMenu.commentId);
              if (row) {
                setEditingCommentId(commentActionMenu.commentId);
                setEditCommentBody(row.body);
              }
            }
            setCommentActionMenu(null);
          }}
        >
          編集
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (commentActionMenu) setCommentDeleteId(commentActionMenu.commentId);
            setCommentActionMenu(null);
          }}
          sx={{ color: 'error.main' }}
        >
          削除
        </MenuItem>
      </Menu>

      <AttachmentImagePreviewDialog
        open={previewImage !== null}
        onClose={() => setPreviewImage(null)}
        src={previewImage?.src ?? null}
        alt={previewImage?.alt ?? ''}
      />

      <Dialog
        open={deleteOpen}
        onClose={() => !deleting && setDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            m: 2,
          },
        }}
      >
        <DialogTitle sx={{ pt: 3, px: 3, pb: 1, typography: 'h6' }}>投稿を削除しますか？</DialogTitle>
        <DialogContent sx={{ px: 3, pt: 1, pb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            この操作は取り消せません。
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5, flexWrap: 'wrap' }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting} sx={{ minWidth: 100 }}>
            キャンセル
          </Button>
          <Button color="error" variant="contained" onClick={() => void handleDelete()} disabled={deleting} sx={{ minWidth: 100 }}>
            削除する
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={commentDeleteId !== null}
        onClose={() => !commentDeleteBusy && setCommentDeleteId(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            m: 2,
          },
        }}
      >
        <DialogTitle sx={{ pt: 3, px: 3, pb: 1, typography: 'h6' }}>コメントを削除しますか？</DialogTitle>
        <DialogContent sx={{ px: 3, pt: 1, pb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            この操作は取り消せません。
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5, flexWrap: 'wrap' }}>
          <Button onClick={() => setCommentDeleteId(null)} disabled={commentDeleteBusy} sx={{ minWidth: 100 }}>
            キャンセル
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => void handleConfirmDeleteComment()}
            disabled={commentDeleteBusy}
            sx={{ minWidth: 100 }}
          >
            削除する
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Box>
  );
};

export default BulletinDetail;
